import { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { UploadCloud, FileText, Download, X, Merge as MergeIcon, Search, MailCheck } from "lucide-react";
import { toast } from "sonner";

function NewUploadMerge() {
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customName, setCustomName] = useState("master-leads");
  const [dedupe, setDedupe] = useState(true);
  const inp = useRef(null);

  const onPick = (list) => {
    if (!list?.length) return;
    setFiles([...files, ...Array.from(list)]);
  };
  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));
  const clearAll = () => setFiles([]);

  const onMerge = async () => {
    if (!files.length) return toast.error("Add at least one file");
    setLoading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      if (customName) fd.append("custom_name", customName);
      fd.append("dedupe_within", String(dedupe));
      const token = localStorage.getItem("omnilink_token") || "";
      const res = await fetch(`${API_BASE}/merge`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        let msg = `Merge failed (${res.status})`;
        try { const j = await res.json(); msg = formatApiError(j?.detail) || msg; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (customName || "master-leads").endsWith(".csv") ? customName : `${customName || "master-leads"}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Merged file downloaded");
    } catch (e) {
      toast.error(e.message || "Merge failed");
    } finally {
      setLoading(false);
    }
  };

  const totalMB = files.reduce((s, f) => s + f.size, 0) / (1024 * 1024);

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white">
          <div
            data-testid="merge-dropzone"
            className={`drop-zone ${drag ? "active" : ""} rounded-sm p-10 text-center cursor-pointer`}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); onPick(e.dataTransfer.files); }}
            onClick={() => inp.current?.click()}
          >
            <UploadCloud className="mx-auto text-[#002FA7]" size={40} />
            <div className="mt-3 font-display text-lg font-semibold text-gray-950">Drop CSV / Excel files</div>
            <div className="text-sm text-gray-500 mt-1">No limit. Files do not need headers.</div>
            <input ref={inp} type="file" multiple accept=".csv,.xlsx,.xls" className="hidden" data-testid="merge-file-input" onChange={(e) => onPick(e.target.files)} />
          </div>

          {files.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500">
                  Queued: {files.length} files · <span className="font-mono">{totalMB.toFixed(1)} MB</span>
                </div>
                <Button variant="outline" size="sm" className="rounded-sm" onClick={clearAll} data-testid="merge-clear">Clear all</Button>
              </div>
              <div className="border border-gray-200 rounded-sm max-h-[420px] overflow-auto" data-testid="merge-files-list">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr className="text-[10px] tracking-wider uppercase text-gray-500">
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">File</th>
                      <th className="text-right px-3 py-2">Size</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((f, i) => (
                      <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-xs text-gray-500">{i + 1}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <FileText size={14} className="text-gray-400" />
                            <span className="truncate max-w-[420px]" title={f.name}>{f.name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{(f.size / 1024).toFixed(1)} KB</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-[#FF3B30]" data-testid={`merge-remove-${i}`}><X size={14} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      </div>

      <MergeOptionsCard
        customName={customName} setCustomName={setCustomName}
        dedupe={dedupe} setDedupe={setDedupe}
        onMerge={onMerge} disabled={files.length === 0} loading={loading}
      />
    </div>
  );
}

function PickExistingMerge() {
  const [allFiles, setAllFiles] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [customName, setCustomName] = useState("master-from-existing");
  const [dedupe, setDedupe] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/files");
        setAllFiles(data.files || []);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return allFiles.filter((f) => {
      if (q && !f.original_name.toLowerCase().includes(q.toLowerCase())) return false;
      if (start && f.source_date < start) return false;
      if (end && f.source_date > end) return false;
      return true;
    });
  }, [allFiles, q, start, end]);

  const toggle = (id) => {
    const n = new Set(picked);
    n.has(id) ? n.delete(id) : n.add(id);
    setPicked(n);
  };
  const allFilteredPicked = filtered.length > 0 && filtered.every((f) => picked.has(f.id));
  const toggleAll = () => {
    const n = new Set(picked);
    if (allFilteredPicked) filtered.forEach((f) => n.delete(f.id));
    else filtered.forEach((f) => n.add(f.id));
    setPicked(n);
  };

  const onMerge = async () => {
    if (picked.size === 0) return toast.error("Pick at least one file");
    setLoading(true);
    try {
      const token = localStorage.getItem("omnilink_token") || "";
      const res = await fetch(`${API_BASE}/merge/from-files`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ file_ids: Array.from(picked), custom_name: customName, dedupe_within: dedupe }),
      });
      if (!res.ok) {
        let msg = `Merge failed (${res.status})`;
        try { const j = await res.json(); msg = formatApiError(j?.detail) || msg; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (customName || "master").endsWith(".csv") ? customName : `${customName || "master"}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Merged file downloaded");
    } catch (e) {
      toast.error(e.message || "Merge failed");
    } finally {
      setLoading(false);
    }
  };

  const totalRows = useMemo(
    () => filtered.filter((f) => picked.has(f.id)).reduce((s, f) => s + (f.total_rows || 0), 0),
    [filtered, picked]
  );

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white lg:col-span-2">
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-gray-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search filename" className="pl-9 h-10 rounded-sm" data-testid="existing-search" />
          </div>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-10 rounded-sm" data-testid="existing-start" />
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-10 rounded-sm" data-testid="existing-end" />
        </div>
        <div className="border border-gray-200 rounded-sm max-h-[500px] overflow-auto" data-testid="existing-files-list">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-[10px] tracking-wider uppercase text-gray-500">
                <th className="text-left px-3 py-2 w-10">
                  <Checkbox checked={allFilteredPicked} onCheckedChange={toggleAll} data-testid="existing-toggle-all" />
                </th>
                <th className="text-left px-3 py-2">File</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-right px-3 py-2">Rows</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (<tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No files</td></tr>)}
              {filtered.map((f) => (
                <tr key={f.id} className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50 ${picked.has(f.id) ? "bg-[#002FA7]/5" : ""}`} onClick={() => toggle(f.id)} data-testid={`existing-row-${f.id}`}>
                  <td className="px-3 py-2"><Checkbox checked={picked.has(f.id)} onCheckedChange={() => toggle(f.id)} /></td>
                  <td className="px-3 py-2 max-w-[360px] truncate" title={f.original_name}>
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-gray-400" />
                      <span className="truncate">{f.original_name}</span>
                      {f.last_sent_at && <MailCheck size={11} className="text-[#00C04B]" title={`Sent ${f.last_sent_at.slice(0,10)}`} />}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{f.source_date}</td>
                  <td className="px-3 py-2 text-right font-mono">{f.total_rows}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 text-sm text-gray-600" data-testid="existing-count">
          {picked.size} files picked · {totalRows.toLocaleString()} rows total
        </div>
      </Card>

      <MergeOptionsCard
        customName={customName} setCustomName={setCustomName}
        dedupe={dedupe} setDedupe={setDedupe}
        onMerge={onMerge} disabled={picked.size === 0} loading={loading}
      />
    </div>
  );
}

function MergeOptionsCard({ customName, setCustomName, dedupe, setDedupe, onMerge, disabled, loading }) {
  return (
    <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white h-fit">
      <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500">Output</div>
      <div className="mt-4 space-y-4">
        <div>
          <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">Master filename</Label>
          <Input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="master-leads" className="mt-1 h-10 rounded-sm font-mono" data-testid="merge-filename" />
          <div className="text-xs text-gray-500 mt-1">.csv appended automatically</div>
        </div>
        <label className="flex items-start gap-2 text-sm" data-testid="merge-dedupe-toggle">
          <input type="checkbox" checked={dedupe} onChange={(e) => setDedupe(e.target.checked)} className="mt-1" />
          <span>
            <span className="font-medium text-gray-900">Deduplicate by phone</span>
            <span className="block text-xs text-gray-500 mt-0.5">Keep first occurrence across files</span>
          </span>
        </label>
        <Button
          onClick={onMerge}
          disabled={loading || disabled}
          className="w-full h-11 rounded-sm bg-[#002FA7] hover:bg-[#002480] text-white"
          data-testid="merge-submit"
        >
          {loading ? "Merging…" : (<><MergeIcon size={16} className="mr-2" /> Merge & Download</>)}
        </Button>
      </div>
    </Card>
  );
}

export default function Merge() {
  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="merge-page">
      <div className="mb-8 fade-up">
        <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gray-500">Combine</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-gray-950 mt-2">Merge Files</h1>
        <p className="text-gray-600 mt-2">
          Combine multiple files into <span className="font-semibold text-gray-900">one master CSV</span>. Upload new files OR pick already-uploaded ones.
        </p>
      </div>

      <Tabs defaultValue="upload" className="w-full" data-testid="merge-tabs">
        <TabsList className="rounded-sm bg-gray-100">
          <TabsTrigger value="upload" data-testid="merge-tab-upload" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:text-[#002FA7]">Upload new files</TabsTrigger>
          <TabsTrigger value="existing" data-testid="merge-tab-existing" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:text-[#002FA7]">Pick already uploaded</TabsTrigger>
        </TabsList>
        <TabsContent value="upload" className="mt-6"><NewUploadMerge /></TabsContent>
        <TabsContent value="existing" className="mt-6"><PickExistingMerge /></TabsContent>
      </Tabs>
    </div>
  );
}
